import { Box, Grid, Paper, Stack, Typography, useMediaQuery } from '@mui/material'
import { FunctionComponent } from 'react'
import { ReductionGraph } from '../../../components/presentation-components/reduction-components/reduction-graph'
import { AnalyticMetric } from '../../../components/presentation-components/analytics-components/graphs/analytic-metric'
import gaiaTheme from '../../../root-components/theme'
import { IColorPalatte, IGaiaReportReductionDetails } from '../shared-services/public-reports.model'
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode'
import {
  getGaiaReportBodyTextSx,
  getGaiaReportSectionContentSx,
  getGaiaReportSectionFrameSx,
  getGaiaReportSectionHeadingSx,
} from './gaia-report-web-page-pdf-styles'
import { useReportContentOverride } from '../shared-services/gaia-report-content'

export const GaiaReportWebPageReduction: FunctionComponent<{
  companyName: string,
  reductionDetails: IGaiaReportReductionDetails,
  colorPalatte: IColorPalatte
}> = ({ companyName, reductionDetails, colorPalatte }) => {

  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const pdfExport = usePdfExportMode();
  const compactBodyFontSize = pdfExport ? '0.95rem' : undefined;

  const td = reductionDetails.reductionTargetsData
  const hasMarketSplit = td.some((d) => d.isMarketBased === true || d.isMarketBased === false)
  const marketRows = hasMarketSplit ? td.filter((d) => d.isMarketBased === true) : td

  const rts = reductionDetails.reductionTargetSettings
  const heading = useReportContentOverride('reduction.heading')
  const body = useReportContentOverride('reduction.body')
  const targetsSummary = useReportContentOverride('reduction.targetsSummary', {
    baseYear: rts?.baseYear,
    targetYear: rts?.targetYear,
    years: Number(rts?.targetYear) - Number(rts?.baseYear),
    annualReductionPercentage: rts?.annualReductionPercentage,
    scope1Percentage: rts?.scopeOneReductionPercentage,
    scope2LocationPercentage: rts?.scopeTwoLocationReductionPercentage,
    scope2MarketPercentage: rts?.scopeTwoMarketReductionPercentage,
    scope3Percentage: rts?.scopeThreeReductionPercentage,
  })

  return (
    <Stack sx={{
      ...getGaiaReportSectionFrameSx({ backgroundColor: 'white', pdfExport }),
      ...(pdfExport ? {} : {
        mt: -5,
        position: 'relative',
        zIndex: 2,
      }),
    }}>
      <Box sx={getGaiaReportSectionHeadingSx({ backgroundColor: 'white', pdfExport, webBottomPadding: 6 })}>
        <Typography variant='h3' sx={{ color: colorPalatte.main, mb: 2 }}>{heading ?? 'Reduction Targets'}</Typography>
      </Box>
      {reductionDetails.reductionTargetsData.length > 0 &&
        <Box sx={getGaiaReportSectionContentSx()}>
          <Typography sx={getGaiaReportBodyTextSx({ color: 'text.secondary', isMobileModeMd, isMobileModeSm, pdfExport, pdfMarginBottom: 1, webMarginBottom: 2 })}>{body ?? `This report provides an overview of ${companyName}'s reduction targets. The bar chart illustrates the company's emissions for each year, while the accompanying line represents the established targets for reducing these emissions over time. This visual representation highlights the progress towards achieving the set reduction goals and serves as a benchmark for future environmental efforts.`}</Typography>
          <Typography sx={{ color: 'text.primary', mb: pdfExport ? 2 : 4, fontSize: compactBodyFontSize }}>
            {targetsSummary ?? <>
            The reduction targets are {reductionDetails.reductionTargetSettings?.calculationMethod === '1.5c' ? `aligned with the Science Based Targets initiative (SBTi) to limit global warming to 1.5°C, as outlined in the Paris Agreement` :
              `custom made by ${companyName}`}, and span {Number(reductionDetails.reductionTargetSettings?.targetYear) - Number(reductionDetails.reductionTargetSettings?.baseYear)} years starting from the base year {reductionDetails.reductionTargetSettings?.baseYear} and aiming for completion in {reductionDetails.reductionTargetSettings?.targetYear}. {reductionDetails.reductionTargetSettings?.calculationMethod === '1.5c' ?
                `The annual reduction percentage is set at ${reductionDetails.reductionTargetSettings?.annualReductionPercentage}%.` :
                `The reduction targets include ${reductionDetails.reductionTargetSettings?.scopeOneReductionPercentage}% for Scope 1, ${reductionDetails.reductionTargetSettings?.scopeTwoLocationReductionPercentage}% for Scope 2 Location-based,  ${reductionDetails.reductionTargetSettings?.scopeTwoMarketReductionPercentage ? reductionDetails.reductionTargetSettings.scopeTwoMarketReductionPercentage : '-'}% for Scope 2 Market-based and ${reductionDetails.reductionTargetSettings?.scopeThreeReductionPercentage}% for Scope 3.`}
            </>}
          </Typography>
          <div className="chart-block">
            <Grid container spacing={pdfExport ? 1.5 : 2}>
              <Grid item xl={4} lg={4} md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <AnalyticMetric
                  compact={pdfExport}
                  title={`Current Emissions, at ${marketRows.filter(data => data.actualValue !== null).length > 0 ? marketRows.filter(data => data.actualValue !== null).reduce((prev, current) => (prev.year > current.year ? prev : current)).year : ''} `}
                  value={marketRows.filter(data => data.actualValue !== null).length > 0 ? marketRows.filter(data => data.actualValue !== null).reduce((prev, current) => (prev.year > current.year ? prev : current)).actualValue as number : 0}
                  units='tCO2e' />
              </Grid>
              <Grid item xl={4} lg={4} md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <AnalyticMetric compact={pdfExport} units='tCO2e'
                  title={`Target Emissions, by ${marketRows.reduce((prev, current) => (prev.year > current.year ? prev : current)).year}`}
                  value={marketRows.reduce((prev, current) => (prev.year > current.year ? prev : current)).targetValue} />
              </Grid>
              <Grid item xl={4} lg={4} md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <AnalyticMetric
                  compact={pdfExport}
                  title={`Reduction Emissions, ${marketRows.filter(data => data.actualValue !== null).length > 0 ? `${Number(marketRows.reduce((prev, current) => (prev.year > current.year ? prev : current)).year) - Number(marketRows.filter(data => data.actualValue !== null).reduce((prev, current) => (prev.year > current.year ? prev : current)).year)} years` : ''}`}
                  negative={true}
                  value={marketRows.filter(data => data.actualValue !== null).length > 0
                    ? Math.max(
                      marketRows.filter(data => data.actualValue !== null).reduce((prev, current) => (prev.year > current.year ? prev : current)).actualValue as number -
                      marketRows.reduce((prev, current) => (prev.year > current.year ? prev : current)).targetValue,
                      0
                    )
                    : 0}
                  units='tCO2e' />
              </Grid>
            </Grid>
          </div>
          <div className="chart-block" style={{ marginTop: pdfExport ? '6px' : '24px' }}>
            <Grid container spacing={2}>
              <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                <Paper variant='outlined' sx={{ p: pdfExport ? 2 : 4, height: pdfExport ? 'auto' : 450, borderRadius: '10px' }}>
                  <ReductionGraph
                    colorPalatte={colorPalatte}
                    graphData={marketRows}
                    leftMarginPx={32}
                    chartHeight={pdfExport ? 190 : undefined}
                    compact={pdfExport}
                  />
                </Paper>
              </Grid>
            </Grid>
          </div>
        </Box>}
    </Stack >
  )
}
